import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { WORKSPACE_SLUG_STORAGE_KEY } from '../api/client';

/**
 * WorkspaceContext — single source of truth for "current workspace" on the
 * React dashboard.
 *
 * Backing data: `user.sites` from `/api/auth/me` (enriched in v1.1, shape
 * `[{ id, slug, companyName }]`). For regular users this is exactly one site;
 * for superadmins the server returns every active site so tenant switching
 * becomes a real feature rather than a stub.
 *
 * Exposed shape:
 *   workspaces : Array<{ id, slug, companyName }>  — sorted by the server
 *   current    : the currently-selected workspace (or null if the user has 0 sites)
 *   switchTo(id)                                   — persist + update selection
 *
 * Persistence: the picked id is stored in `localStorage["lk_workspace_id"]`
 * so reloading the dashboard keeps the superadmin on the same tenant. On
 * logout we let the key linger (harmless — next login will either re-pick
 * the same site if it's still valid, or fall back to the first one).
 *
 * Provider lives below <AuthProvider> in App.jsx (see the provider tree note
 * there), so `useAuth()` is always available. When `user` is null (logged
 * out / still loading) we fall back to an empty sites array, which keeps
 * the public /login route safe from crashing on `useWorkspace()`.
 */

const STORAGE_KEY = 'lk_workspace_id';
const WorkspaceContext = createContext(null);

function readStoredId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    // SSR / privacy mode / storage disabled — treat as "no persisted pick".
    return null;
  }
}

function writeStoredId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function writeStoredSlug(slug) {
  try {
    if (slug) localStorage.setItem(WORKSPACE_SLUG_STORAGE_KEY, slug);
    else localStorage.removeItem(WORKSPACE_SLUG_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function persistWorkspace(site) {
  writeStoredId(site?.id || null);
  writeStoredSlug(site?.slug || null);
}

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();

  // Memoize the sites array so effects/memos downstream don't re-run on
  // every render just because we re-derived a new array literal. `user` is
  // only swapped after login/logout/refresh, so this identity is stable
  // across ordinary re-renders.
  const sites = useMemo(
    () => (Array.isArray(user?.sites) ? user.sites : []),
    [user],
  );

  // Seed from localStorage; fall back to the first available site. Using a
  // lazy initializer keeps localStorage read out of subsequent renders.
  const [currentId, setCurrentId] = useState(() => readStoredId() || sites[0]?.id || null);

  // If the user's sites list changes (login, logout, superadmin switches
  // account), verify the persisted id still points at a valid workspace.
  // Invalid → silently snap to the first site (and persist that choice).
  // Persist in a layout effect so descendant screen effects cannot issue a
  // site-scoped request before X-Site-Slug is available to apiFetch.
  useLayoutEffect(() => {
    if (sites.length === 0) {
      // No sites available yet (user=null or empty sites) — keep whatever
      // we had; once sites arrive the next effect run will re-pick.
      return;
    }
    const valid = currentId && sites.some((s) => s.id === currentId);
    if (!valid) {
      const fallback = sites[0];
      setCurrentId(fallback.id);
      persistWorkspace(fallback);
      return;
    }
    persistWorkspace(sites.find((s) => s.id === currentId));
  }, [sites, currentId]);

  const switchTo = useCallback(
    (id) => {
      if (!id) return;
      const site = sites.find((s) => s.id === id);
      if (!site) return;
      setCurrentId(id);
      persistWorkspace(site);
    },
    [sites],
  );

  const current = useMemo(() => {
    if (sites.length === 0) return null;
    return sites.find((s) => s.id === currentId) || sites[0];
  }, [sites, currentId]);

  const value = useMemo(
    () => ({
      workspaces: sites,
      current,
      switchTo,
    }),
    [sites, current, switchTo],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be inside WorkspaceProvider');
  return ctx;
}
